import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Bell, Plus, X, PencilSimple, Trash, Play, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import client from "../lib/api";

/**
 * AlertsPage — pilots save Load Alert criteria (origin/destination state,
 * equipment keyword, min $/mile or min total rate). When a matching load is
 * created via POST /loads, the backend fires an email to the pilot instantly.
 */
export default function AlertsPage() {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await client.get("/load-alerts");
      setAlerts(Array.isArray(data) ? data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load alerts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = (a) => { setEditing(a); setEditorOpen(true); };

  const handleDelete = async (id) => {
    if (!confirm("Delete this alert? You'll stop receiving emails for its criteria.")) return;
    try {
      await client.delete(`/load-alerts/${id}`);
      toast.success("Alert deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    }
  };

  const handleToggle = async (a) => {
    try {
      await client.patch(`/load-alerts/${a.id}`, { active: !a.active });
      toast.success(a.active ? "Alert paused" : "Alert resumed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const handleTest = async (id) => {
    try {
      const { data } = await client.post(`/load-alerts/${id}/test`);
      if (data.matches) {
        toast.success(`✓ Would match load ${data.load_number} (${data.origin} → ${data.destination})`);
      } else if (data.reason) {
        toast(`No loads yet to test against — create a load first`);
      } else {
        toast(`Latest load ${data.load_number} does NOT match this alert`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test failed");
    }
  };

  return (
    <div className="p-4 sm:p-6 md:p-8" data-testid="alerts-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 mb-8">
        <div>
          <div className="flex items-center gap-2.5 mb-1.5">
            <div className="w-9 h-9 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
              <Bell size={18} weight="fill" />
            </div>
            <h1 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight">Load Alerts</h1>
          </div>
          <p className="text-neutral-500 text-sm max-w-xl">
            Get emailed the instant a load matching your criteria is created. Uses your real
            EIA fuel + auto-calculated deadhead for smarter matching.
          </p>
        </div>
        <button
          onClick={openNew}
          data-testid="alerts-new-btn"
          className="rounded-full px-4 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] inline-flex items-center gap-2 whitespace-nowrap"
        >
          <Plus size={14} weight="bold" /> New alert
        </button>
      </div>

      {/* Empty state */}
      {!loading && alerts.length === 0 && (
        <div
          data-testid="alerts-empty"
          className="rounded-2xl border border-dashed border-white/10 bg-white/[0.02] p-10 text-center"
        >
          <Bell size={36} weight="light" className="text-neutral-600 mx-auto mb-3" />
          <div className="text-white font-medium mb-1">No alerts saved yet</div>
          <p className="text-neutral-500 text-sm max-w-md mx-auto mb-5">
            Set criteria like "Reefer loads out of TX at $2.50/mi or better" and you'll get an
            email the moment a matching load lands in your dispatch board.
          </p>
          <button
            onClick={openNew}
            className="rounded-full px-5 py-2.5 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] inline-flex items-center gap-2"
          >
            <Plus size={14} weight="bold" /> Create your first alert
          </button>
        </div>
      )}

      {/* List */}
      {alerts.length > 0 && (
        <div
          className="space-y-3"
          data-testid="alerts-list"
          data-loading={loading ? "true" : "false"}
          data-count={alerts.length}
        >
          {alerts.map((a) => (
            <AlertRow
              key={a.id}
              alert={a}
              onEdit={() => openEdit(a)}
              onDelete={() => handleDelete(a.id)}
              onToggle={() => handleToggle(a)}
              onTest={() => handleTest(a.id)}
            />
          ))}
        </div>
      )}

      {loading && (
        <div
          data-testid="alerts-loading"
          role="status"
          aria-live="polite"
          className="text-neutral-500 text-sm"
        >
          Loading alerts…
        </div>
      )}

      {/* Editor Modal */}
      {editorOpen && (
        <AlertEditor
          initial={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function AlertRow({ alert, onEdit, onDelete, onToggle, onTest }) {
  const criteria = [
    alert.origin_state && `From ${alert.origin_state}`,
    alert.destination_state && `→ ${alert.destination_state}`,
    alert.equipment_keyword && `"${alert.equipment_keyword}"`,
    alert.min_rate_per_mile && `≥ $${Number(alert.min_rate_per_mile).toFixed(2)}/mi`,
    alert.min_total_rate && `≥ $${Number(alert.min_total_rate).toLocaleString()} total`,
  ].filter(Boolean);
  const lastMatch = alert.last_match_at
    ? new Date(alert.last_match_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })
    : null;

  return (
    <div
      data-testid={`alert-row-${alert.id}`}
      className={`rounded-xl border p-4 sm:p-5 transition-colors ${
        alert.active
          ? "border-white/10 bg-white/[0.02] hover:border-white/20"
          : "border-white/[0.06] bg-white/[0.01] opacity-60"
      }`}
    >
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 mb-1">
            {alert.active ? (
              <span className="w-2 h-2 rounded-full bg-emerald-400" />
            ) : (
              <span className="w-2 h-2 rounded-full bg-neutral-600" />
            )}
            <h3 className="text-white font-medium text-base truncate">{alert.name}</h3>
          </div>
          <div className="flex flex-wrap gap-2 mb-2">
            {criteria.length === 0 ? (
              <span className="text-xs text-neutral-500 italic">Any load (no criteria set)</span>
            ) : (
              criteria.map((c, i) => (
                <span key={i} className="text-xs bg-white/[0.05] border border-white/10 rounded-full px-2.5 py-1 text-neutral-300 font-mono">
                  {c}
                </span>
              ))
            )}
          </div>
          <div className="text-xs text-neutral-500">
            {lastMatch ? (
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle size={12} weight="fill" className="text-emerald-400" />
                Last match: {lastMatch}
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5">
                <WarningCircle size={12} className="text-neutral-600" />
                No matches yet
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={onTest}
            title="Test against most recent load"
            data-testid={`alert-test-${alert.id}`}
            className="text-neutral-400 hover:text-white p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
          >
            <Play size={16} />
          </button>
          <button
            onClick={onToggle}
            title={alert.active ? "Pause" : "Resume"}
            data-testid={`alert-toggle-${alert.id}`}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              alert.active
                ? "border-white/15 text-neutral-300 hover:bg-white/[0.05]"
                : "border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/[0.08]"
            }`}
          >
            {alert.active ? "Pause" : "Resume"}
          </button>
          <button
            onClick={onEdit}
            title="Edit"
            data-testid={`alert-edit-${alert.id}`}
            className="text-neutral-400 hover:text-white p-2 rounded-lg hover:bg-white/[0.05] transition-colors"
          >
            <PencilSimple size={16} />
          </button>
          <button
            onClick={onDelete}
            title="Delete"
            data-testid={`alert-delete-${alert.id}`}
            className="text-neutral-400 hover:text-red-400 p-2 rounded-lg hover:bg-red-500/[0.08] transition-colors"
          >
            <Trash size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}

function AlertEditor({ initial, onClose, onSaved }) {
  const isEdit = Boolean(initial?.id);
  const [name, setName] = useState(initial?.name || "");
  const [originState, setOriginState] = useState(initial?.origin_state || "");
  const [destState, setDestState] = useState(initial?.destination_state || "");
  const [keyword, setKeyword] = useState(initial?.equipment_keyword || "");
  const [minRpm, setMinRpm] = useState(initial?.min_rate_per_mile ?? "");
  const [minTotal, setMinTotal] = useState(initial?.min_total_rate ?? "");
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Give the alert a name"); return; }
    setSaving(true);
    try {
      const payload = {
        name: name.trim(),
        origin_state: originState.trim() || null,
        destination_state: destState.trim() || null,
        equipment_keyword: keyword.trim() || null,
        min_rate_per_mile: minRpm === "" ? null : Number(minRpm),
        min_total_rate: minTotal === "" ? null : Number(minTotal),
      };
      if (isEdit) {
        await client.patch(`/load-alerts/${initial.id}`, payload);
        toast.success("Alert updated");
      } else {
        await client.post("/load-alerts", { ...payload, active: true });
        toast.success("Alert created — you'll be emailed on first match");
      }
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[60] flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="alert-editor-modal"
    >
      <div
        className="bg-[#0f1116] border border-white/10 rounded-2xl w-full max-w-lg overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between p-5 border-b border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-[#FF5A1F]/15 text-[#FF5A1F] flex items-center justify-center">
              <Bell size={16} weight="fill" />
            </div>
            <div className="text-white font-semibold text-sm">
              {isEdit ? "Edit alert" : "New load alert"}
            </div>
          </div>
          <button onClick={onClose} data-testid="alert-editor-close" className="text-neutral-500 hover:text-white p-1">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <Field label="Alert name *">
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. TX Reefer $2.50+"
              data-testid="alert-name"
              required
              maxLength={80}
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Origin state">
              <input
                value={originState}
                onChange={(e) => setOriginState(e.target.value.slice(0, 2).toUpperCase())}
                placeholder="TX (blank = any)"
                data-testid="alert-origin-state"
                maxLength={2}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 uppercase font-mono"
              />
            </Field>
            <Field label="Destination state">
              <input
                value={destState}
                onChange={(e) => setDestState(e.target.value.slice(0, 2).toUpperCase())}
                placeholder="GA (blank = any)"
                data-testid="alert-dest-state"
                maxLength={2}
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 uppercase font-mono"
              />
            </Field>
          </div>
          <Field label="Equipment / customer keyword" hint="matches customer name or notes (case-insensitive)">
            <input
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="reefer, flatbed, hazmat…"
              data-testid="alert-keyword"
              className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60"
            />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Min $/mile" hint="e.g. 2.50">
              <input
                value={minRpm}
                onChange={(e) => setMinRpm(e.target.value)}
                type="number"
                step="0.01"
                min="0"
                placeholder="2.50"
                data-testid="alert-min-rpm"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
              />
            </Field>
            <Field label="Min total rate" hint="OR match if total ≥">
              <input
                value={minTotal}
                onChange={(e) => setMinTotal(e.target.value)}
                type="number"
                step="1"
                min="0"
                placeholder="2000"
                data-testid="alert-min-total"
                className="w-full bg-black/40 border border-white/10 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-[#FF5A1F]/60 font-mono"
              />
            </Field>
          </div>
          <div className="text-xs text-neutral-500 leading-relaxed bg-white/[0.02] rounded-lg border border-white/10 p-3">
            <b className="text-neutral-300">How it works:</b> Blank fields = wildcard. When a load is
            created that satisfies ALL your specified criteria, you get an email at your account
            address within seconds. Same alert won't fire twice within 15 min.
          </div>
          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="text-neutral-400 hover:text-white text-sm px-3 py-2 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              data-testid="alert-save-btn"
              className="rounded-full px-5 py-2 text-sm font-semibold bg-[#FF5A1F] text-black hover:bg-[#FF7038] disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {saving ? "Saving…" : isEdit ? "Save changes" : "Create alert"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="flex items-baseline justify-between mb-1.5">
        <span className="text-xs text-neutral-400">{label}</span>
        {hint && <span className="text-[10px] text-neutral-600">{hint}</span>}
      </label>
      {children}
    </div>
  );
}
